import { Typography } from '@material-ui/core';

import {
  ConfidenceWebsiteLink,
  DialogCompositeMui as DialogComposite,
  FormFieldsBoxMui as FormFieldsBox,
  MultiStepDialogMui as MultiStepDialog,
  useFormFieldsContext,
} from '@spotify/confidence-core-react';
import { MetricsV1MetricInput } from '@spotify/confidence-graphql';
import {
  MeasurementFormValues,
  MetricFilters,
  MetricInputsForm,
  StepId,
  metricFilterHasEmptyAttributes,
} from '@spotify/plugin-confidence-metrics-react';

import { useDeriveMeasurementName } from '../useAutoDeriveMeasurementName';

type InputsStepProps = {
  stepId?: string;
};

// TODO: MetricInputsForm is currently context-agnostic (uses value/onChange props)
// because it's shared across multiple form patterns. Investigate moving form field
// access into the form components themselves so this step doesn't need to bridge.
export const InputsStep = ({ stepId = StepId.Inputs }: InputsStepProps) => {
  const { values, setValues } = useFormFieldsContext<MeasurementFormValues>();
  const { entity, factTable, filter } = values;

  const canProceed = Boolean(
    entity && factTable && !metricFilterHasEmptyAttributes(filter),
  );

  const { deriveName } = useDeriveMeasurementName();

  const handleMetricChange = (newMetric: MetricsV1MetricInput) => {
    const factTableChanged = values.factTable !== newMetric.factTable;
    const shouldDerive = factTableChanged || !values.displayNameManuallyEdited;

    setValues({
      entity: newMetric.entity ?? undefined,
      factTable: newMetric.factTable ?? undefined,
      filter: newMetric.filter,
      typeSpec: newMetric.typeSpec ?? undefined,
      ...(factTableChanged && { displayNameManuallyEdited: false }),
    });

    if (shouldDerive && newMetric.factTable) {
      deriveName(newMetric.factTable);
    }
  };

  const handleFilterChange = (newFilter: MeasurementFormValues['filter']) => {
    setValues({ filter: newFilter });
  };

  const currentMetric: MetricsV1MetricInput = {
    displayName: values.displayName || '',
    owner: values.owner || '',
    entity: values.entity || '',
    factTable: values.factTable || '',
    filter: values.filter,
    typeSpec: values.typeSpec,
  };

  return (
    <MultiStepDialog.Step id={stepId}>
      <MultiStepDialog.Header>
        <Typography variant="h4" component="h4">
          Inputs
        </Typography>
        <DialogComposite.Subtitle>
          Select the entity you want to measure and the fact table with the
          measurements you want to use.{' '}
          <ConfidenceWebsiteLink route="/docs/metrics" underline="always">
            Learn more.
          </ConfidenceWebsiteLink>
        </DialogComposite.Subtitle>
      </MultiStepDialog.Header>
      <MultiStepDialog.Body>
        <FormFieldsBox>
          <MetricInputsForm
            value={currentMetric}
            onChange={handleMetricChange}
          />
          <MetricFilters
            factTable={values.factTable || ''}
            filter={values.filter}
            onChange={handleFilterChange}
          />
        </FormFieldsBox>
      </MultiStepDialog.Body>
      <MultiStepDialog.Actions>
        <MultiStepDialog.Actions.Prev />
        <MultiStepDialog.Actions.Next disabled={!canProceed} />
      </MultiStepDialog.Actions>
    </MultiStepDialog.Step>
  );
};
